import { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import SeoHead from '@/components/base/SeoHead';
import { OrganizationSchema, WebSiteSchema, WebPageSchema, FaqPageSchema } from '@/components/base/SchemaOrg';
import { categories, resources, faqItems, stats } from '@/mocks/resources';

export default function HomePage() {
  const featuredResources = resources.filter((r) => r.isFeatured);
  const latestResources = resources.filter((r) => r.isLatest);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <Layout>
      <SeoHead
        title="无码专区 - 精选资源聚合平台"
        description="无码专区是专为内容爱好者打造的精选资源聚合平台，汇聚海量高清无水印素材，分类清晰、更新及时。无论您是设计师、创作者还是普通用户，无码专区均可满足您对高品质内容的一切需求，立即探索无码专区全库资源。"
        keywords="无码专区,无码专区高清资源,无码专区免费下载,无码专区最新更新,无码专区素材合集,无码专区精选推荐,无码专区资源导航"
        canonicalUrl="/"
        ogImage="https://readdy.ai/api/search-image?query=Modern%20resource%20aggregation%20platform%20with%20clean%20minimal%20design%20showing%20digital%20assets%20icons%20illustrations%20and%20creative%20tools%20on%20warm%20background%20professional%20website%20homepage%20banner&width=1200&height=630&seq=og-home&orientation=landscape"
      />
      <OrganizationSchema />
      <WebSiteSchema />
      <WebPageSchema
        name="无码专区首页"
        description="无码专区是专为内容爱好者打造的精选资源聚合平台，汇聚海量高清无水印素材。"
        url="https://aialhku.cn/"
      />

      <section className="relative w-full h-[520px] md:h-[620px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20artistic%20geometric%20composition%20with%20warm%20orange%20amber%20and%20cream%20tones%20overlapping%20circles%20and%20soft%20gradients%20modern%20minimal%20design%20clean%20aesthetic%20digital%20art%20with%20smooth%20textures%20and%20organic%20shapes%20elegant%20composition&width=1600&height=900&seq=hero-bg-home&orientation=landscape"
            alt="无码专区 - 精选资源聚合平台"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
        </div>
        <div className="relative z-10 w-full px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-4 leading-tight">
              无码专区 — 精选资源聚合平台
            </h1>
            <p className="text-base md:text-lg text-background-50/80 mb-8 max-w-xl mx-auto leading-relaxed">
              汇聚海量高清无水印素材，分类清晰、更新及时。为设计师、创作者提供一站式高品质资源服务，立即探索全库资源。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to={`/category/${categories[0].slug}`}
                className="px-8 py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                立即探索资源
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 border border-background-50/40 text-background-50 rounded-lg font-medium text-sm hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer"
              >
                了解更多
              </Link>
            </div>

            <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl mx-auto">
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-background-50 font-heading">{stats.totalResources.toLocaleString()}+</div>
                <div className="text-xs text-background-50/60 mt-1">资源总数</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-background-50 font-heading">{stats.totalDownloads.toLocaleString()}+</div>
                <div className="text-xs text-background-50/60 mt-1">累计下载</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-background-50 font-heading">{stats.totalUsers.toLocaleString()}+</div>
                <div className="text-xs text-background-50/60 mt-1">活跃用户</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-background-50 font-heading">{stats.dailyUpdates}+</div>
                <div className="text-xs text-background-50/60 mt-1">每日更新</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              资源分类导航
            </h2>
            <p className="text-sm text-foreground-500 max-w-lg mx-auto">
              六大资源分类，精准满足不同创作需求。点击分类开始探索。
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group bg-background-50 border border-background-200/70 rounded-xl p-5 text-center hover:border-primary-300 transition-all duration-300 cursor-pointer"
              >
                <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-3 rounded-xl bg-primary-50 flex items-center justify-center group-hover:bg-primary-100 transition-colors">
                  <i className={`${cat.icon} text-2xl text-primary-600`}></i>
                </div>
                <h3 className="font-heading text-sm font-bold text-foreground-900 mb-1 whitespace-nowrap">{cat.name}</h3>
                <p className="text-xs text-foreground-500">{cat.count.toLocaleString()} 个资源</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              精选推荐
            </h2>
            <p className="text-sm text-foreground-500 max-w-lg mx-auto">
              编辑团队精心挑选的优质资源，每一份都值得收藏
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-product-shop>
            {featuredResources.map((resource) => (
              <Link
                key={resource.id}
                to={`/article/${resource.id}`}
                className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
              >
                <div className="relative w-full h-48 overflow-hidden">
                  <img
                    src={resource.image}
                    alt={resource.title}
                    title={`${resource.title} - 无码专区精选推荐`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="px-2.5 py-1 bg-accent-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
                      {resource.category}
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-heading text-sm font-bold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {resource.title}
                  </h3>
                  <p className="text-xs text-foreground-500 line-clamp-2 mb-3">{resource.description}</p>
                  <div className="flex items-center justify-between text-xs text-foreground-400">
                    <span><i className="ri-eye-line mr-1"></i>{resource.views.toLocaleString()}</span>
                    <span><i className="ri-download-line mr-1"></i>{resource.downloads.toLocaleString()}</span>
                    <span>{resource.date}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              最新更新
            </h2>
            <p className="text-sm text-foreground-500 max-w-lg mx-auto">
              每日精选新鲜上架资源，紧跟创作潮流不掉队
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-product-shop>
            {latestResources.concat(
              resources.filter((r) => !r.isFeatured && !r.isLatest).slice(0, 4 - latestResources.length)
            ).slice(0, 4).map((resource) => (
              <Link
                key={resource.id}
                to={`/article/${resource.id}`}
                className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
              >
                <div className="relative w-full h-48 overflow-hidden">
                  <img
                    src={resource.image}
                    alt={resource.title}
                    title={`${resource.title} - 无码专区最新更新`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 right-3">
                    <span className="px-2 py-0.5 bg-primary-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">新</span>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-heading text-sm font-bold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {resource.title}
                  </h3>
                  <p className="text-xs text-foreground-500 line-clamp-2 mb-3">{resource.description}</p>
                  <div className="flex items-center justify-between text-xs text-foreground-400">
                    <span><i className="ri-calendar-line mr-1"></i>{resource.date}</span>
                    <span><i className="ri-download-line mr-1"></i>{resource.downloads.toLocaleString()}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              为什么选择无码专区？
            </h2>
            <p className="text-sm text-foreground-500 max-w-lg mx-auto">
              四大核心优势，让创作更轻松高效
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: 'ri-shield-check-line', title: '全站免费', desc: '所有资源完全免费下载，无隐藏收费。我们坚信优质创作资源应该对所有人开放。' },
              { icon: 'ri-refresh-line', title: '每日更新', desc: '编辑团队每日精选10-20个优质资源上架，确保内容库始终新鲜丰富。' },
              { icon: 'ri-stack-line', title: '海量资源', desc: '平台已收录4300+优质资源，覆盖设计、影视、音乐、教程等六大热门领域。' },
              { icon: 'ri-user-star-line', title: '严格审核', desc: '每个资源上架前均经过人工审核，确保质量达标、版权清晰、内容真实有效。' },
            ].map((item, idx) => (
              <div key={idx} className="text-center p-6">
                <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-accent-100 flex items-center justify-center">
                  <i className={`${item.icon} text-2xl text-accent-600`}></i>
                </div>
                <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">{item.title}</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-50" id="faq">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              常见问题
            </h2>
            <p className="text-sm text-foreground-500">
              关于无码专区，你可能想了解的内容
            </p>
          </div>
          <FaqPageSchema faqs={faqItems} />
          <div className="space-y-3">
            {faqItems.map((faq, idx) => (
              <div key={idx} className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
                <button
                  className="w-full px-5 py-4 flex items-center justify-between text-left cursor-pointer hover:bg-background-100/50 transition-colors"
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                >
                  <h4 className="font-heading text-sm font-bold text-foreground-900 pr-4">{faq.question}</h4>
                  <i className={`ri-${openFaq === idx ? 'subtract' : 'add'}-line text-lg text-foreground-500 flex-shrink-0 transition-transform`}></i>
                </button>
                {openFaq === idx && (
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-primary-500">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-4">
            准备好开始探索了吗？
          </h2>
          <p className="text-sm text-background-50/80 mb-8 max-w-md mx-auto">
            4300+ 优质资源正在等你，全部免费，立即开始你的创作之旅
          </p>
          <Link
            to={`/category/${categories[0].slug}`}
            className="inline-flex items-center px-8 py-3 bg-background-50 text-primary-600 rounded-lg font-medium text-sm hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            免费探索资源
            <i className="ri-arrow-right-line ml-2"></i>
          </Link>
        </div>
      </section>
    </Layout>
  );
}